import { useTranslation } from 'react-i18next';

import { OnboardingDialog } from './onboarding-dialog';
import { useUserContext } from '../../../Contexts/User';
import { V2_ONBOARDING_CUTOFF_DATE } from '@/new/constants/onboarding';

interface MatterOverviewOnboardingProps {
  onTakeTour?: () => void;
}

/**
 * Onboarding dialog for the Matter overview page.
 * Only shown to existing users (created before Nov 28, 2025) to explain the V2 changes.
 */
export function MatterOverviewOnboarding({ onTakeTour }: MatterOverviewOnboardingProps) {
  const { user } = useUserContext();

  // Only show V2 onboarding to existing users (created before cutoff date)
  const userCreatedAt = user?.createdAt ? new Date(user.createdAt) : null;
  const isExistingUser = userCreatedAt && userCreatedAt < V2_ONBOARDING_CUTOFF_DATE;

  if (!isExistingUser) {
    return null;
  }

  const { t } = useTranslation();

  return (
    <OnboardingDialog
      dialogId="matter-overview"
      icon="lucide--layout-dashboard"
      title="Matter Overview"
      onTakeTour={onTakeTour}
    >
      <h3 className="text-lg font-medium">Welcome to the Matter overview page</h3>
      <p>
        This page provides an overview for your Matter, showing high level information about the data in your Matter.
      </p>
      <p>
        You can view all of the data in this Matter (e.g. facts, chronologies) by navigating to them in the sidebar.
      </p>
      <p>
        If you want to {t('analyse')} data from just a subset of documents, you can create a <strong>Subset</strong> to
        focus your analysis.
      </p>
    </OnboardingDialog>
  );
}
